/**
 * MasterGanttPage.tsx
 * Phase 4, Item 1 — Master Schedule page shell.
 * Phase 4, Item 3 — Today line + Critical Path lines wired in via ScheduleGantt props.
 * Phase 4, Item 4 — Conflict markers: fetch conflicts in parallel, tag points,
 *                    render glyphs on bars, click navigates to Machine Schedule.
 * Phase 4, Item 5 — Toolbar: filters (Lab Section / Machine / Work Type / Clear),
 *                    Toggle Proposals switch, Multi-select toggle, Global Export.
 * Phase 4, Item 6 — Right rail: legend, summary totals, skill-level guide.
 * Phase 4, Item 9 — Auto-refresh every 60 s; "Last synchronized: X mins ago" footer.
 * Phase 4, Item 10 — Deep links: clicking any non-conflict bar navigates to the
 *                    source (Machine Schedule / Employee Schedule / Work Orders /
 *                    Projects).  resolveDeepLinkRoute() is the pure helper.
 *
 * Layout:
 *   MasterGanttToolbar — all controls in two rows
 *   Gantt area         — Highcharts Gantt via ScheduleGantt wrapper
 *   Right rail         — MasterGanttRightRail (legend + summary + skill guide)
 *   Footer             — LastSyncedFooter (item 9)
 *
 * Color semantics: applied via theme.schedule.* tokens throughout.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import ScheduleGantt from '../../../components/schedule/ScheduleGantt.js';
import {
  buildGanttChartData,
  computeCriticalPathDates,
  ganttStructureSignature,
  type GanttColorTokens,
  type CriticalPathEntry,
  type GanttChartData,
  type GanttGroupBy,
} from '../../../components/schedule/masterGanttRowBuilder.js';
import {
  GanttDetailDrawer,
  type GanttBarDetail,
} from '../../../components/schedule/GanttDetailDrawer.js';
import { tagConflicts } from '../../../components/schedule/ganttConflictTagger.js';
import {
  fetchMasterGanttData,
  defaultMasterGanttRange,
} from '../../../services/scheduleMasterGantt.js';
import { fetchScheduleConflicts } from '../../../services/scheduleBookings.js';
import type { MasterGanttResponse, ConflictSummary } from '../../../types/schedule.js';
import { useScheduleHolidays } from '../../../hooks/useScheduleHolidays.js';
import { MasterGanttToolbar } from './MasterGanttToolbar.js';
import { useMasterGanttFilters } from './useMasterGanttFilters.js';
import { responseToCsv, triggerCsvDownload } from './masterGanttCsvExport.js';
import {
  applyMasterGanttFilterState,
  collectMachinesForFilter,
} from './masterGanttHelpers.js';
import { GANTT_ROW_HEIGHT_PX } from '../../../components/schedule/ganttRowHeightSync.js';
import { useElementContentHeight } from '../../../hooks/useElementContentHeight.js';
import { MasterGanttRightRail } from './MasterGanttRightRail.js';
import { useAutoRefresh } from '../../../hooks/useAutoRefresh.js';
import { LastSyncedFooter } from '../../../components/schedule/LastSyncedFooter.js';
import {
  resolveDeepLinkRoute,
  buildSearch,
  findBarContext,
  buildBarDetail,
  type DeepLinkPoint,
} from './masterGanttDeepLinks.js';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const MasterGanttPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  // ── Date range state ──────────────────────────────────────────────────────
  const [dateRange, setDateRange] = useState(() => defaultMasterGanttRange());

  // P2-21 (audit #2): stat-holiday feed for the detail drawer's "busy N working
  // day(s)" staffing row — the same window the Gantt renders, so the count
  // agrees with the holiday-aware Employee Schedule surfaces.
  const { holidays: rangeHolidays } = useScheduleHolidays(dateRange.startDate, dateRange.endDate);
  const holidayDateSet = useMemo(
    () => new Set(rangeHolidays.map((h) => h.date.slice(0, 10))),
    [rangeHolidays],
  );

  // ── Data state ────────────────────────────────────────────────────────────
  const [ganttData, setGanttData] = useState<MasterGanttResponse | null>(null);
  const [conflicts, setConflicts] = useState<ConflictSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Read-only details drawer state ────────────────────────────────────────
  // Clicking a leaf / conflict bar opens a read-only summary instead of hard-
  // navigating. `detailPoint` retains the clicked point's deep-link shape so the
  // drawer's "Open in Schedule" button can reuse resolveDeepLinkRoute() and fire
  // the SAME focus param (focusBookingKey / focusUserKey) that highlights the row.
  const [detail, setDetail] = useState<GanttBarDetail | null>(null);
  const [detailPoint, setDetailPoint] = useState<DeepLinkPoint | null>(null);

  // ── Phase 4 Item 5: filter + multi-select state ───────────────────────────
  const {
    filterState,
    setFilter,
    clearFilters,
    hasActiveFilters,
    multiSelectOn,
    selectedKeys,
    setMultiSelectOn,
    addSelection,
    clearSelection,
  } = useMasterGanttFilters();

  const selectedCount = selectedKeys.size;

  // ── Phase 4 Item 5: chart ref for export ──────────────────────────────────
  const chartRef = useRef<HighchartsReact.RefObject | null>(null);

  // ── BUG-2026-0194: the chart fills the Gantt area and scrolls INSIDE ─────
  // Rows are a fixed 26 px (GANTT_ROW_HEIGHT_PX); the chart is sized to the
  // space the page gives it, so the navigator / horizontal scrollbar stay at
  // the bottom of the viewable window and the date axis stays at the top.
  // 0 until the first layout measurement → hold the chart until measured.
  const [ganttAreaRef, ganttViewportPx] = useElementContentHeight<HTMLDivElement>();

  // ── REQ-03: Group-by mode (Project | Machine), persisted to localStorage ──
  // Mirrors the legend-collapse / density persistence pattern used elsewhere
  // in the schedule views. Default 'machine' = the lab's preferred layout; only
  // an explicit saved 'project' choice opts out.
  const [groupBy, setGroupBy] = useState<GanttGroupBy>(() =>
    localStorage.getItem('belab.schedule.gantt.groupBy') === 'project'
      ? 'project'
      : 'machine',
  );
  const handleGroupByChange = useCallback((next: GanttGroupBy) => {
    setGroupBy(next);
    try {
      localStorage.setItem('belab.schedule.gantt.groupBy', next);
    } catch {
      /* localStorage blocked (private mode) — keep in-memory state only. */
    }
  }, []);

  // ── Right-rail (legend) collapse — UX request ─────────────────────────────
  // Persisted so the choice survives navigation. When the rail collapses, the
  // Gantt area (flex:1) reclaims its width; Highcharts only resizes on reflow,
  // so we reflow once the slide animation settles. reflow()/setData never
  // re-init the treegrid yAxis, so this stays clear of the BUG-2026-0093
  // listener-stacking path.
  const [legendCollapsed, setLegendCollapsed] = useState<boolean>(
    () => localStorage.getItem('belab.schedule.gantt.legendCollapsed') === '1',
  );
  const toggleLegendCollapsed = useCallback(() => {
    setLegendCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem('belab.schedule.gantt.legendCollapsed', next ? '1' : '0');
      return next;
    });
  }, []);
  useEffect(() => {
    const settleMs = theme.transitions.duration.standard + 30;
    const id = window.setTimeout(() => {
      chartRef.current?.chart?.reflow();
    }, settleMs);
    return () => window.clearTimeout(id);
  }, [legendCollapsed, theme.transitions.duration.standard]);

  // ── Color tokens for the row builder ─────────────────────────────────────
  // BUG-2026-0080: memoise so chartData's useMemo doesn't invalidate every
  // render (theme.schedule.* tokens are stable across renders unless the
  // theme itself changes).
  const colors = useMemo<GanttColorTokens>(() => ({
    committed: theme.schedule.committed,
    tentative: theme.schedule.tentative,
    machineTime: theme.schedule.machineTime,
    employeeAlloc: theme.schedule.employeeAlloc,
    maintenance: theme.schedule.maintenance,
    outOfService: theme.schedule.outOfService,
    projectEnvelope: theme.schedule.projectEnvelope,
    workOrderEnvelope: theme.schedule.workOrderEnvelope,
    continuous: theme.schedule.continuous,
  }), [
    theme.schedule.committed,
    theme.schedule.tentative,
    theme.schedule.machineTime,
    theme.schedule.employeeAlloc,
    theme.schedule.maintenance,
    theme.schedule.outOfService,
    theme.schedule.projectEnvelope,
    theme.schedule.workOrderEnvelope,
    theme.schedule.continuous,
  ]);

  // ── Fetch data (gantt + conflicts in parallel) ───────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, conflictData] = await Promise.all([
        fetchMasterGanttData(dateRange.startDate, dateRange.endDate),
        fetchScheduleConflicts({
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
        }),
      ]);
      setGanttData(data);
      setConflicts(conflictData);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load schedule data.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  // ── Phase 4 Item 9: Auto-refresh every 60 s ──────────────────────────────
  // `refresh` is also wired to the toolbar's manual Refresh button so that
  // clicking it also updates the "Last synchronized" timestamp.
  const { lastSyncedAt, refresh: autoRefresh } = useAutoRefresh(loadData, 60_000);

  // ── Quick-range presets (toolbar dropdown) ────────────────────────────────
  // Set both dates AND load. autoRefresh runs the latest loadData via a ref that
  // only updates after this render commits, so calling it synchronously here
  // would fetch the STALE range. Defer the apply to an effect keyed on dateRange
  // (gated by a flag so manual picker edits don't trigger an auto-load).
  const applyOnNextRangeRef = useRef(false);
  const applyRangePreset = useCallback((startDate: string, endDate: string) => {
    applyOnNextRangeRef.current = true;
    setDateRange({ startDate, endDate });
  }, []);
  useEffect(() => {
    if (!applyOnNextRangeRef.current) return;
    applyOnNextRangeRef.current = false;
    void autoRefresh();
  }, [dateRange, autoRefresh]);

  // ── Build machine list for filters (from ganttData) ───────────────────────
  const machinesForFilter = useMemo(() => collectMachinesForFilter(ganttData), [ganttData]);

  // ── Apply filters client-side to ganttData before building chart ──────────
  // BUG-2026-0207: every URL-sticky filter (Toggle Proposals, Lab Section,
  // Machine, Work Type, then Quote / Work Order — BUG-2026-0079) runs through
  // ONE pure helper. The previous inline pass silently skipped `labSections`
  // (its comment claimed the response had no lab-section data; the API has
  // returned `machineLabSection` on every booking since Phase 4), so the Lab
  // Section dropdown was a no-op on this route.
  // BUG-2026-0080: memoised so the response identity is stable when no filter
  // actually changes (`filterState` is itself memoised on the URL params).
  const filteredGanttData = useMemo<MasterGanttResponse | null>(
    () => (ganttData ? applyMasterGanttFilterState(ganttData, filterState) : null),
    [ganttData, filterState],
  );

  // ── Build Highcharts series from filtered data, then tag conflict markers ─
  // BUG-2026-0080: memoise so we don't rebuild the entire Highcharts series
  // (and re-walk the conflict map) on every render. Depends only on the
  // filtered response, conflicts, and the theme-driven color tokens.
  const chartData = useMemo<GanttChartData | null>(() => {
    if (!filteredGanttData) return null;
    const base = buildGanttChartData(filteredGanttData, colors, groupBy);
    if (conflicts) {
      return tagConflicts(base, conflicts);
    }
    return base;
  }, [filteredGanttData, conflicts, colors, groupBy]);

  // ── Master Gantt crash fix (Work Type / machine filters) ──────────────────
  // Two guards against the Highcharts treegrid morph crash ("Cannot read
  // properties of undefined (reading 'x')" in generateTick/addLabel):
  //   1. structureKey — when a filter changes WHICH rows exist, the treegrid
  //      id/parent set changes; we key the chart on it so the structure change
  //      forces a clean remount instead of an in-place series.setData morph
  //      (the same remedy already used for the group-by toggle). Pure attribute
  //      changes on the same rows keep the key stable → imperative sync animates.
  //   2. hasBars — when a filter leaves zero booking/assignment bars, show an
  //      empty state instead of mounting a degenerate (envelope-only) treegrid.
  const structureKey = useMemo(
    () => ganttStructureSignature(chartData?.series[0]?.data ?? []),
    [chartData],
  );
  const hasBars = useMemo(
    () =>
      !!chartData?.series[0]?.data.some(
        (p) => p.taskType === 'machineBooking' || p.taskType === 'employeeAssignment',
      ),
    [chartData],
  );

  // ── Critical Path entries — Phase 4, Item 3 ──────────────────────────────
  // BUG-2026-0093: memoise. Previously this recomputed a *new array* on every
  // render, which gave ScheduleGantt a new `criticalPaths` prop each time and
  // forced its `plotLines`/`options` memos to rebuild — making the chart's
  // `options` identity churn on every render. That drove a `chart.update()` per
  // render (see ScheduleGantt), which re-inits the treegrid yAxis and stacks a
  // `beforeRedraw` listener each time → super-linear node multiplication → OOM.
  const criticalPaths = useMemo<CriticalPathEntry[]>(
    () => (filteredGanttData ? computeCriticalPathDates(filteredGanttData) : []),
    [filteredGanttData],
  );

  // ── Conflict glyph colors — Phase 4, Item 4 ──────────────────────────────
  // BUG-2026-0093: memoise so the prop identity is stable across renders. An
  // inline `{{ hard, soft }}` literal was a fresh object every render, which
  // (like criticalPaths above) invalidated ScheduleGantt's `options` memo on
  // every render. theme.schedule.* tokens are stable unless the theme changes.
  const conflictColors = useMemo(
    () => ({
      hard: theme.schedule.conflictHard,
      soft: theme.schedule.conflictSoft,
    }),
    [theme.schedule.conflictHard, theme.schedule.conflictSoft],
  );

  // ── Unified click handler ─────────────────────────────────────────────────
  //
  // Click priority:
  //   1. Multi-select mode  — adds to selection set, no drawer/navigation.
  //   2. Leaf / conflict bar — open the read-only details drawer (NO auto-jump).
  //                            Conflict bars still open the drawer (with a
  //                            conflict note); the drawer's button does the jump.
  //   3. Group rows         — no-op (no drawer, no navigation).
  //
  // The actual deep-link navigation (+ row highlight) now lives behind the
  // drawer's "Open in Schedule" button (see onOpenInSchedule below), reusing
  // resolveDeepLinkRoute() so focusBookingKey / focusUserKey still fire.
  const handlePointClick = useCallback(
    (point: Highcharts.Point) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Highcharts point has dynamic custom props
      const p = point as any;

      // Priority 1: multi-select mode intercepts all clicks.
      // BUG-2026-0194: a merged task bar folds several WEEKLY assignment rows
      // (custom.assignmentKeys) — select every one so a bulk commit covers the
      // whole bar, not just its first week.
      // BUG-2026-0193: a folded "Preparation" bar lists every prep booking it
      // stands for (custom.bookingKeys) — select them all.
      if (multiSelectOn) {
        const keys: string[] = p.custom?.bookingKey
          ? ((p.custom?.bookingKeys as string[] | undefined) ?? [p.custom.bookingKey])
          : (p.custom?.assignmentKeys as string[] | undefined)
            ?? (p.custom?.assignmentKey ? [p.custom.assignmentKey] : []);
        for (const key of keys) addSelection(key);
        return;
      }

      // Priority 2: leaf / conflict bars — build context and open the drawer.
      const deepLinkPoint: DeepLinkPoint = {
        taskType: p.taskType ?? '',
        custom: p.custom ?? {},
      };
      const ctx = findBarContext(deepLinkPoint, filteredGanttData);
      if (!ctx) return; // group rows / unknown types — no-op

      const conflictType: 'hard' | 'soft' | null = p.custom?.conflictType ?? null;
      // Only offer "Open in Schedule" when resolveDeepLinkRoute can build a target.
      const canOpenInSchedule = resolveDeepLinkRoute(deepLinkPoint) !== null;

      const built: GanttBarDetail = buildBarDetail(
        ctx,
        p.name,
        conflictType,
        canOpenInSchedule,
      );

      setDetailPoint(deepLinkPoint);
      setDetail(built);
    },
    [multiSelectOn, addSelection, filteredGanttData],
  );

  // ── Drawer close / open-in-schedule handlers ──────────────────────────────
  const handleCloseDetail = useCallback(() => {
    setDetail(null);
    setDetailPoint(null);
  }, []);

  const handleOpenInSchedule = useCallback(() => {
    if (detailPoint) {
      const descriptor = resolveDeepLinkRoute(detailPoint);
      if (descriptor) {
        const search = { ...descriptor.search };
        // For a machine booking, also pin the Machine Schedule's visible window
        // around the booking, so a bar outside the default 2-week window still
        // lands in view (and the focus-flash has a bar to scroll to) instead of
        // silently no-op-ing. detail.start/end are UTC-midnight ms (toMs), so
        // whole-day shifts are timezone-safe. Item 6.2: skip for Index bars —
        // they deep-link to the Scenario Planner, which has no such window param.
        if (
          detailPoint.taskType === 'machineBooking' &&
          !detailPoint.custom?.isIndexAllocation &&
          detail?.start != null
        ) {
          const DAY = 86_400_000;
          const endMs = detail.end ?? detail.start;
          const iso = (ms: number) => new Date(ms).toISOString().slice(0, 10);
          search.start = iso(detail.start - 3 * DAY);
          search.end = iso(endMs + 11 * DAY);
        }
        navigate(descriptor.path + buildSearch(search));
      }
    }
    setDetail(null);
    setDetailPoint(null);
  }, [detailPoint, detail, navigate]);

  // ── Follow-up 1.5: Bulk commit success / error handlers ───────────────────

  /**
   * Called by MasterGanttBulkActions after a successful bulk commit.
   * Refreshes the gantt data (so bars flip to Committed) and clears the
   * selection (multi-select mode stays ON so the user can keep selecting).
   */
  const handleBulkCommitSuccess = useCallback(() => {
    clearSelection();
    void autoRefresh();
  }, [clearSelection, autoRefresh]);

  const handleBulkError = useCallback((message: string) => {
    setError(message);
  }, []);

  // ── Phase 4 Item 5: export handlers ───────────────────────────────────────

  const handleExportChart = useCallback(
    (type: 'image/png' | 'application/pdf') => {
      const chart = chartRef.current?.chart;
      if (!chart) return;
      chart.exportChart({ type }, {});
    },
    [],
  );

  const handleExportCsv = useCallback(() => {
    if (!ganttData) return;
    const csv = responseToCsv(ganttData);
    const now = new Date().toISOString().slice(0, 10);
    triggerCsvDownload(csv, `master-gantt-${now}.csv`);
  }, [ganttData]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* ── Toolbar (Phase 4 Item 5) ───────────────────────────────────────── */}
      <MasterGanttToolbar
        startDate={dateRange.startDate}
        endDate={dateRange.endDate}
        onStartDateChange={(val) =>
          setDateRange((prev) => ({ ...prev, startDate: val }))
        }
        onEndDateChange={(val) =>
          setDateRange((prev) => ({ ...prev, endDate: val }))
        }
        onApply={() => void autoRefresh()}
        onRefresh={() => void autoRefresh()}
        loading={loading}
        onRangePreset={applyRangePreset}
        groupBy={groupBy}
        onGroupByChange={handleGroupByChange}
        filterState={filterState}
        machines={machinesForFilter}
        hasActiveFilters={hasActiveFilters}
        onFilterChange={setFilter}
        onClearFilters={clearFilters}
        multiSelectOn={multiSelectOn}
        selectedCount={selectedCount}
        selectedKeys={selectedKeys}
        onMultiSelectToggle={setMultiSelectOn}
        ganttData={ganttData}
        onBulkCommitSuccess={handleBulkCommitSuccess}
        onBulkError={handleBulkError}
        onExportChart={handleExportChart}
        onExportCsv={handleExportCsv}
      />

      {/* ── Error banner ─────────────────────────────────────────────────── */}
      {error && (
        <Alert severity="error" sx={{ mx: 2, mt: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ── Main body ────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {/* Gantt area — BUG-2026-0194: the chart fits this box and scrolls its
            rows internally (see ScheduleGantt `fixedRowHeight`), so the box
            itself must not scroll or the navigator would drift off-screen. */}
        <Box
          ref={ganttAreaRef}
          sx={{ flex: 1, minWidth: 0, minHeight: 0, overflow: 'hidden', p: 1, position: 'relative' }}
        >
          {/* Initial-load spinner only — shown when there's no prior data to
              keep displayed. */}
          {loading && !ganttData && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 400,
                gap: 2,
              }}
            >
              <CircularProgress size={32} />
              <Typography color="text.secondary">Loading schedule…</Typography>
            </Box>
          )}

          {/*
            BUG-2026-0076: Keep ScheduleGantt mounted during refreshes.
            Previously this was gated on `!loading`, so the Highcharts
            instance was unmounted+remounted on every Refresh / auto-refresh —
            destroying the user's navigator/scrollbar zoom and snapping the
            visible window back to the props-driven default range (which
            Highcharts then displays at monthly tick granularity for a
            multi-month default).

            Now the chart stays alive across refreshes; a non-blocking
            "Refreshing…" pill in the top-right communicates the fetch.
          */}
          {filteredGanttData && chartData && hasBars && ganttViewportPx > 0 && (
            <ScheduleGantt
              // BUG-2026-0093 (treegrid morph crash): REMOUNT the chart whenever
              // the treegrid STRUCTURE changes — a Project<->Machine group-by
              // toggle OR a filter (Work Type / machine) that prunes whole
              // branches. Both produce a different treegrid id/parent set;
              // morphing between them in place via series.setData drives
              // Highcharts' treegrid onBeforeRender into a tick-label rebuild
              // that dereferences a stale node ("Cannot read properties of
              // undefined (reading 'x')" in generateTick/addLabel; also
              // 'setAttribute' / 'removePoint' variants), blanking the page.
              // Keying on `groupBy` + a structure signature forces a clean
              // destroy+recreate when the row set changes, while same-structure
              // data/range refreshes keep the same instance and still flow
              // through useGanttImperativeSync (animation + zoom preserved).
              key={`${groupBy}:${structureKey}`}
              series={chartData.series}
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              // BUG-2026-0194: viewport height + fixed 26 px rows. Rows scroll
              // inside the chart; collapsing shortens the list instead of
              // stretching the remaining rows (was rows × 26 + 100, page-scrolled).
              height={ganttViewportPx}
              fixedRowHeight={GANTT_ROW_HEIGHT_PX}
              criticalPaths={criticalPaths}
              conflictColors={conflictColors}
              onPointClick={handlePointClick}
              chartRef={chartRef}
            />
          )}

          {/* Empty state — data loaded but the current filters leave no bars.
              Showing this (instead of mounting an envelope-only treegrid) keeps
              the page from rendering a confusing tree of empty rows and avoids
              feeding Highcharts a degenerate chart. */}
          {!loading && filteredGanttData && chartData && !hasBars && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: 400,
                gap: 1.5,
                textAlign: 'center',
              }}
            >
              <Typography color="text.secondary">
                No scheduled work matches the current filters.
              </Typography>
              {hasActiveFilters && (
                <Button size="small" variant="outlined" onClick={clearFilters}>
                  Clear filters
                </Button>
              )}
            </Box>
          )}

          {/* Non-blocking refresh indicator overlaid on the chart so the user
              still sees progress without remounting the chart. */}
          {loading && ganttData && (
            <Box
              sx={{
                position: 'absolute',
                top: 12,
                right: 16,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.25,
                py: 0.5,
                borderRadius: '999px',
                bgcolor: 'background.paper',
                border: `1px solid ${theme.border.color}`,
                boxShadow: 1,
                pointerEvents: 'none',
                zIndex: 4,
              }}
              role="status"
              aria-live="polite"
            >
              <CircularProgress size={14} />
              <Typography variant="caption" fontWeight={600} color="text.secondary">
                Refreshing…
              </Typography>
            </Box>
          )}
        </Box>

        {/* ── Right rail (Phase 4, Item 6) — collapsible ────────────────── */}
        <MasterGanttRightRail
          data={ganttData}
          collapsed={legendCollapsed}
          onToggleCollapsed={toggleLegendCollapsed}
        />
      </Box>

      {/* ── Phase 4 Item 9: Last synchronized footer ──────────────────────── */}
      <Box sx={{ px: 2, pb: 0.5 }}>
        <LastSyncedFooter lastSyncedAt={lastSyncedAt} />
      </Box>

      {/* ── Read-only details drawer (click a bar) ────────────────────────── */}
      <GanttDetailDrawer
        open={!!detail}
        detail={detail}
        onClose={handleCloseDetail}
        onOpenInSchedule={handleOpenInSchedule}
        holidays={holidayDateSet}
      />
    </Box>
  );
};
